import { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import { env } from '@/config/env'

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string
          callback: (token: string) => void
          'expired-callback'?: () => void
          'error-callback'?: () => void
        }
      ) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

type TurnstileGlobal = typeof globalThis & {
  turnstile: Window['turnstile'] | undefined
}

const globalWindow = globalThis as TurnstileGlobal

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script'
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

interface TurnstileWidgetProps {
  readonly onToken: (token: string | null) => void
}

export function TurnstileWidget({ onToken }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)
  const [ready, setReady] = useState(false)

  const siteKey = env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY.trim()

  useEffect(() => {
    if (!siteKey) return

    const renderWidget = () => {
      if (!containerRef.current || !globalWindow.turnstile) return
      if (widgetIdRef.current) {
        globalWindow.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
      widgetIdRef.current = globalWindow.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => onToken(token),
        'expired-callback': () => onToken(null),
        'error-callback': () => onToken(null),
      })
      setReady(true)
    }

    const existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      if (globalWindow.turnstile) renderWidget()
      else existing.addEventListener('load', renderWidget)
      return () => existing.removeEventListener('load', renderWidget)
    }

    const script = document.createElement('script')
    script.id = TURNSTILE_SCRIPT_ID
    script.src = TURNSTILE_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.addEventListener('load', renderWidget)
    document.head.appendChild(script)

    return () => {
      script.removeEventListener('load', renderWidget)
      if (widgetIdRef.current && globalWindow.turnstile) {
        globalWindow.turnstile.remove(widgetIdRef.current)
      }
    }
  }, [siteKey, onToken])

  if (!siteKey) return null

  return (
    <Box
      ref={containerRef}
      sx={{ minHeight: ready ? undefined : 65, my: 2, display: 'flex', justifyContent: 'center' }}
      aria-label="Human verification"
    />
  )
}
